import { useState, useEffect } from "react";
import {
    LayoutDashboard,
    Users,
    UserPlus,
    UsersRound,
    Building2,
    MapPin,
    Bell,
    ShoppingBag,
    ShoppingCart,
    CalendarDays,
    BarChart3,
    Package,
    Settings2,
    BookOpen,
    Warehouse,
    ShieldCheck,
    UserCog,
    KeyRound,
    Map,
    Settings,
    LogOut,
    X,
    ChevronDown,
    ListChecks,
    Upload,
    Milestone,
} from "lucide-react";
import { NAV_GROUPS } from "../nav.js";

const S = 17; // default icon size

const icons = {
    dashboard: <LayoutDashboard size={S} />,
    crm: <Users size={S} />,
    customers: <Users size={S} />,
    "new-customers": <UserPlus size={S} />,
    "team-customers": <UsersRound size={S} />,
    organisations: <Building2 size={S} />,
    visits: <MapPin size={S} />,
    alerts: <Bell size={S} />,

    sales: <ShoppingBag size={S} />,
    orders: <ShoppingBag size={S} />,
    "orders-group": <ShoppingBag size={S} />,
    "place-order": <ShoppingCart size={S} />,
    "order-approvals?type=new": <ShieldCheck size={S} />,
    "order-approvals?type=edit": <ListChecks size={S} />,
    calendar: <CalendarDays size={S} />,
    reports: <BarChart3 size={S} />,

    products: <Package size={S} />,
    "products/customizations": <Settings2 size={S} />,

    accounts: <BookOpen size={S} />,
    warehouse: <Warehouse size={S} />,

    admin: <ShieldCheck size={S} />,
    staffs: <UserCog size={S} />,
    "team-management": <UsersRound size={S} />,
    roles: <KeyRound size={S} />,
    areas: <Map size={S} />,

    "crm-call-management": <ListChecks size={S} />,
    "visit-management": <MapPin size={S} />,
    "system-settings": <Settings2 size={S} />,
    "customer-import": <Upload size={S} />,
    settings: <Settings size={S} />,
    logout: <LogOut size={S} />,
    close: <X size={S} />,
    chevron: <ChevronDown size={14} />,
    location: <MapPin size={S} />,
    "live-tracker": <MapPin size={S} />,
    "distance-history": <Milestone size={S} />,
};

/* ── Active item resolution ──────────────────────────────────────── */
const allItemIds = NAV_GROUPS.flatMap((g) =>
    g.items.flatMap((i) => [i.id, ...(i.children || []).map((c) => c.id)]),
);

function isItemActive(itemId, activePage) {
    if (itemId.includes("?")) {
        const [path, search] = itemId.split("?");
        if (path !== activePage) return false;
        const currentSearch = window.location.search.substring(1);
        const itemParams = new URLSearchParams(search);
        const currentParams = new URLSearchParams(currentSearch);
        for (const [key, val] of itemParams.entries()) {
            if (currentParams.get(key) !== val) return false;
        }
        return true;
    }
    if (itemId === activePage) return true;
    const exactExists = allItemIds.includes(activePage);
    if (
        !exactExists &&
        !itemId.includes("/") &&
        activePage.startsWith(itemId + "/")
    )
        return true;
    return false;
}

function isGroupActive(group, activePage) {
    return group.items.some(
        (item) =>
            isItemActive(item.id, activePage) ||
            (item.children || []).some((c) => isItemActive(c.id, activePage)),
    );
}

function isParentActive(item, activePage) {
    return (
        isItemActive(item.id, activePage) ||
        (item.children || []).some((c) => isItemActive(c.id, activePage))
    );
}

/* ═══════════════════════════════════════════════════════════════════
   SIDEBAR COMPONENT
═══════════════════════════════════════════════════════════════════ */
export default function Sidebar({
    activePage,
    onNavigate,
    allowedNav = [],
    isOpen,
    onClose,
}) {
    const [expanded, setExpanded] = useState({}); // group-level
    const [expandedItem, setExpandedItem] = useState({}); // item-level (children)

    // Auto-expand group + parent item that owns the active page
    useEffect(() => {
        NAV_GROUPS.forEach((group) => {
            if (isGroupActive(group, activePage)) {
                setExpanded((prev) => ({ ...prev, [group.id]: true }));
                group.items.forEach((item) => {
                    if (
                        (item.children || []).some((c) =>
                            isItemActive(c.id, activePage),
                        )
                    ) {
                        setExpandedItem((prev) => ({
                            ...prev,
                            [item.id]: true,
                        }));
                    }
                });
            }
        });
    }, [activePage]);

    const toggle = (groupId) =>
        setExpanded((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    const toggleItem = (itemId) =>
        setExpandedItem((prev) => ({ ...prev, [itemId]: !prev[itemId] }));

    const handleNav = (id) => {
        onNavigate(id);
        onClose && onClose();
    };

    // Determine which groups to show:
    // - Groups with allowed items → show filtered items
    // - Placeholder groups (accounts, warehouse) → always show if user has admin privileges
    const isAdmin = allowedNav.includes("system-settings"); // admin-only route as signal
    const visibleGroups = NAV_GROUPS.filter((group) => {
        if (group.items.length === 0) return isAdmin; // placeholder groups
        return group.items.some((item) => allowedNav.includes(item.id));
    });

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={onClose}
                    style={{ display: "block" }}
                />
            )}

            <aside className={`sidebar ${isOpen ? "open" : ""}`}>
                {/* Logo */}
                <div
                    className="sidebar-logo"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                    }}
                >
                    <div>
                        {/* <div className="brand"><span>M</span>eetel</div> */}
                        <img
                            style={{
                                width: "100%",
                                height: "40%",
                                filter: "brightness(0) invert(1)",
                            }}
                            src="/name.png"
                            alt="Meetel"
                        />
                        {/* <div className="sub">Sales Management System</div> */}
                    </div>
                    <button
                        onClick={onClose}
                        className="sidebar-close-btn"
                        style={{
                            display: "none",
                            background: "rgba(255,255,255,.15)",
                            border: "none",
                            color: "#fff",
                            cursor: "pointer",
                            padding: 6,
                            borderRadius: 8,
                        }}
                    >
                        {icons.close}
                    </button>
                </div>

                <nav
                    className="sidebar-nav"
                    style={{ padding: "8px 0", overflowY: "auto", flex: 1 }}
                >
                    {/* Dashboard — standalone */}
                    {allowedNav.includes("dashboard") && (
                        <button
                            className={
                                activePage === "dashboard" ? "active" : ""
                            }
                            onClick={() => handleNav("dashboard")}
                            style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "9px 18px",
                                background:
                                    activePage === "dashboard"
                                        ? "rgba(255,255,255,0.08)"
                                        : "none",
                                border: "none",
                                cursor: "pointer",
                                textAlign: "left",
                                borderRadius: 0,
                                color:
                                    activePage === "dashboard"
                                        ? "#fff"
                                        : "rgba(255,255,255,0.72)",
                                transition: "background 0.15s, color 0.15s",
                            }}
                        >
                            {icons.dashboard}
                            <span style={{ fontSize: 13.5, fontWeight: 500 }}>
                                Dashboard
                            </span>
                        </button>
                    )}

                    {/* Customers — standalone */}
                    {allowedNav.includes("customers") && (
                        <button
                            onClick={() => handleNav("customers")}
                            style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "9px 18px",
                                background:
                                    activePage === "customers"
                                        ? "rgba(255,255,255,0.08)"
                                        : "none",
                                border: "none",
                                cursor: "pointer",
                                textAlign: "left",
                                borderRadius: 0,
                                color:
                                    activePage === "customers"
                                        ? "#fff"
                                        : "rgba(255,255,255,0.72)",
                                transition: "background 0.15s, color 0.15s",
                            }}
                        >
                            {icons.customers}
                            <span
                                style={{
                                    fontSize: 13.5,
                                    fontWeight:
                                        activePage === "customers" ? 600 : 500,
                                }}
                            >
                                Customers
                            </span>
                        </button>
                    )}

                    {/* Section label */}
                    <div
                        style={{
                            padding: "10px 18px 4px",
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            color: "rgba(255,255,255,0.35)",
                            textTransform: "uppercase",
                            marginTop: 4,
                        }}
                    >
                        Main Menu
                    </div>

                    {/* Grouped nav items */}
                    {visibleGroups.map((group) => {
                        const allowedItems = group.items.filter((item) =>
                            allowedNav.includes(item.id),
                        );
                        const isOpen_ = expanded[group.id];
                        const groupIsActive = isGroupActive(group, activePage);
                        const isPlaceholder = group.items.length === 0;

                        return (
                            <div key={group.id}>
                                {/* Group header button */}
                                <button
                                    onClick={() => toggle(group.id)}
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        padding: "9px 18px",
                                        background:
                                            groupIsActive && !isOpen_
                                                ? "rgba(255,255,255,0.08)"
                                                : "none",
                                        border: "none",
                                        cursor: "pointer",
                                        color: groupIsActive
                                            ? "#fff"
                                            : "rgba(255,255,255,0.72)",
                                        textAlign: "left",
                                        borderRadius: 0,
                                        transition:
                                            "background 0.15s, color 0.15s",
                                    }}
                                >
                                    <span
                                        style={{
                                            opacity: groupIsActive ? 1 : 0.8,
                                        }}
                                    >
                                        {icons[group.icon]}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 13.5,
                                            fontWeight: groupIsActive
                                                ? 600
                                                : 500,
                                            flex: 1,
                                        }}
                                    >
                                        {group.label}
                                    </span>
                                    {isPlaceholder ? (
                                        <span
                                            style={{
                                                fontSize: 9,
                                                fontWeight: 700,
                                                letterSpacing: "0.04em",
                                                padding: "2px 6px",
                                                borderRadius: 99,
                                                background:
                                                    "rgba(255,255,255,0.12)",
                                                color: "rgba(255,255,255,0.5)",
                                            }}
                                        >
                                            SOON
                                        </span>
                                    ) : (
                                        <span
                                            style={{
                                                transform: isOpen_
                                                    ? "rotate(0deg)"
                                                    : "rotate(-90deg)",
                                                transition: "transform 0.2s",
                                                opacity: 0.7,
                                            }}
                                        >
                                            {icons.chevron}
                                        </span>
                                    )}
                                </button>

                                {/* Sub-items */}
                                {isOpen_ && !isPlaceholder && (
                                    <div
                                        style={{
                                            paddingLeft: 32,
                                            paddingBottom: 4,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        {allowedItems.map((item) => {
                                            const active = isItemActive(
                                                item.id,
                                                activePage,
                                            );
                                            const hasChildren =
                                                item.children?.length > 0;
                                            const allowedChildren = (
                                                item.children || []
                                            ).filter((c) =>
                                                allowedNav.includes(c.id),
                                            );
                                            const itemOpen =
                                                expandedItem[item.id];
                                            const parentActive = isParentActive(
                                                item,
                                                activePage,
                                            );

                                            return (
                                                <div key={item.id}>
                                                    <button
                                                        onClick={() => {
                                                            handleNav(item.id);
                                                            if (hasChildren)
                                                                toggleItem(
                                                                    item.id,
                                                                );
                                                        }}
                                                        style={{
                                                            width: "100%",
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 8,
                                                            padding: "7px 12px",
                                                            background: active
                                                                ? "rgba(255,255,255,0.12)"
                                                                : "none",
                                                            border: "none",
                                                            borderLeft:
                                                                parentActive
                                                                    ? "2px solid rgba(255,255,255,0.7)"
                                                                    : "2px solid transparent",
                                                            cursor: "pointer",
                                                            color: parentActive
                                                                ? "#fff"
                                                                : "rgba(255,255,255,0.62)",
                                                            textAlign: "left",
                                                            borderRadius:
                                                                "0 6px 6px 0",
                                                            fontSize: 13,
                                                            fontWeight:
                                                                parentActive
                                                                    ? 600
                                                                    : 400,
                                                            transition:
                                                                "all 0.15s",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                opacity:
                                                                    parentActive
                                                                        ? 1
                                                                        : 0.7,
                                                            }}
                                                        >
                                                            {icons[item.id] ||
                                                                icons.settings}
                                                        </span>
                                                        <span
                                                            style={{ flex: 1 }}
                                                        >
                                                            {item.label}
                                                        </span>
                                                        {hasChildren &&
                                                            allowedChildren.length >
                                                                0 && (
                                                                <span
                                                                    style={{
                                                                        transform:
                                                                            itemOpen
                                                                                ? "rotate(0deg)"
                                                                                : "rotate(-90deg)",
                                                                        transition:
                                                                            "transform 0.2s",
                                                                        opacity: 0.6,
                                                                    }}
                                                                >
                                                                    <ChevronDown
                                                                        size={
                                                                            12
                                                                        }
                                                                    />
                                                                </span>
                                                            )}
                                                    </button>

                                                    {/* Children (level 3) */}
                                                    {hasChildren &&
                                                        itemOpen &&
                                                        allowedChildren.length >
                                                            0 && (
                                                            <div
                                                                style={{
                                                                    paddingLeft: 18,
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "column",
                                                                    gap: 1,
                                                                    paddingBottom: 2,
                                                                }}
                                                            >
                                                                {allowedChildren.map(
                                                                    (child) => {
                                                                        const childActive =
                                                                            isItemActive(
                                                                                child.id,
                                                                                activePage,
                                                                            );
                                                                        return (
                                                                            <button
                                                                                key={
                                                                                    child.id
                                                                                }
                                                                                onClick={() =>
                                                                                    handleNav(
                                                                                        child.id,
                                                                                    )
                                                                                }
                                                                                style={{
                                                                                    display:
                                                                                        "flex",
                                                                                    alignItems:
                                                                                        "center",
                                                                                    gap: 7,
                                                                                    padding:
                                                                                        "6px 10px",
                                                                                    background:
                                                                                        childActive
                                                                                            ? "rgba(255,255,255,0.1)"
                                                                                            : "none",
                                                                                    border: "none",
                                                                                    borderLeft:
                                                                                        childActive
                                                                                            ? "2px solid rgba(255,255,255,0.5)"
                                                                                            : "2px solid transparent",
                                                                                    cursor: "pointer",
                                                                                    color: childActive
                                                                                        ? "#fff"
                                                                                        : "rgba(255,255,255,0.5)",
                                                                                    textAlign:
                                                                                        "left",
                                                                                    borderRadius:
                                                                                        "0 6px 6px 0",
                                                                                    fontSize: 12,
                                                                                    fontWeight:
                                                                                        childActive
                                                                                            ? 600
                                                                                            : 400,
                                                                                    transition:
                                                                                        "all 0.15s",
                                                                                }}
                                                                            >
                                                                                <span
                                                                                    style={{
                                                                                        opacity:
                                                                                            childActive
                                                                                                ? 1
                                                                                                : 0.65,
                                                                                    }}
                                                                                >
                                                                                    {icons[
                                                                                        child
                                                                                            .id
                                                                                    ] || (
                                                                                        <Upload
                                                                                            size={
                                                                                                13
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </span>
                                                                                {
                                                                                    child.label
                                                                                }
                                                                            </button>
                                                                        );
                                                                    },
                                                                )}
                                                            </div>
                                                        )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Placeholder "coming soon" content */}
                                {isOpen_ && isPlaceholder && (
                                    <div
                                        style={{
                                            paddingLeft: 36,
                                            paddingBottom: 8,
                                            paddingTop: 2,
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 12,
                                                color: "rgba(255,255,255,0.3)",
                                                fontStyle: "italic",
                                            }}
                                        >
                                            Coming soon…
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Settings — standalone at bottom of list */}
                    {allowedNav.includes("settings") && (
                        <>
                            <div
                                style={{
                                    margin: "8px 18px",
                                    borderTop:
                                        "1px solid rgba(255,255,255,0.08)",
                                }}
                            />
                            <button
                                onClick={() => handleNav("settings")}
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    padding: "9px 18px",
                                    background:
                                        activePage === "settings"
                                            ? "rgba(255,255,255,0.08)"
                                            : "none",
                                    border: "none",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    borderRadius: 0,
                                    color:
                                        activePage === "settings"
                                            ? "#fff"
                                            : "rgba(255,255,255,0.72)",
                                    transition: "background 0.15s, color 0.15s",
                                }}
                            >
                                {icons.settings}
                                <span
                                    style={{
                                        fontSize: 13.5,
                                        fontWeight:
                                            activePage === "settings"
                                                ? 600
                                                : 500,
                                    }}
                                >
                                    Settings
                                </span>
                            </button>
                        </>
                    )}

                    {/* Logout */}
                    <div
                        style={{
                            margin: "4px 18px",
                            borderTop: "1px solid rgba(255,255,255,0.08)",
                        }}
                    />
                    <button
                        className="logout-btn"
                        onClick={() => handleNav("login")}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "9px 18px",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "rgba(255,255,255,0.6)",
                            textAlign: "left",
                            borderRadius: 0,
                        }}
                    >
                        {icons.logout}
                        <span style={{ fontSize: 13.5, fontWeight: 500 }}>
                            Logout
                        </span>
                    </button>
                </nav>
            </aside>
        </>
    );
}
